// import components
import {
  ControlledSelect,
  ControlledToggleButtonGroup
} from 'components/shared';
import { Skeleton, Tooltip, Typography } from '@mui/material';
import { ControlledReadOnlyField } from 'components/shared/ControlledFields/ControlledReadOnlyField';
import { StyledBoxFlexCenter } from 'components/shared/Box/Box.styles';
import { Icon } from '@sdb/planning-ui';

// import enums
import { EndOfEmploymentSettlementEnums } from 'components/admin-portal/CLA/CLASettings/CLASettings.enums';

// import external libraries
import { useTranslation } from 'react-i18next';

// import styles
import { StyledSettlementsDivider } from './EndOfEmploymentSettlementsForm.styles';

// import types
import { SettlementFormValues } from './EndOfEmploymentSettlementsForm.types';
import { SelectSettlementTypeProps } from './SelectSettlementType.types';

// import utils
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useFormContext } from 'react-hook-form';
import { buildDutyCodeKey } from './SelectSettlementType.utils';

const SelectSettlementType = ({
  endOfEmploymentData,
  isLoading
}: SelectSettlementTypeProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'endOfEmploymentSettlement'
  });
  const { transferableEmployments } = endOfEmploymentData ?? {};
  const { control, watch, setValue } = useFormContext<SettlementFormValues>();
  const today = dayjs().toDate();
  const validWorkAgreements = transferableEmployments?.filter(
    (agreement) =>
      dayjs(agreement.startDate).toDate() <= today &&
      dayjs(agreement.endDate).toDate() >= today
  );
  const watchEndOfEmploymentSettlementType = watch(
    'endOfEmploymentSettlementType'
  );
  const [settlementType, setSettlementType] = useState<string | null>(
    watchEndOfEmploymentSettlementType?.toLowerCase?.()
  );

  const isTransferSettlementType =
    watchEndOfEmploymentSettlementType ===
    EndOfEmploymentSettlementEnums.TRANSFER;

  const showWhenNonZero = (val: number | string | undefined) =>
    Math.abs(Number(val) || 0) >= 0.01;

  const hasSettlementType =
    settlementType !== EndOfEmploymentSettlementEnums?.NONE?.toLowerCase?.();

  const tooltipTitle = (() => {
    switch (watchEndOfEmploymentSettlementType) {
      case EndOfEmploymentSettlementEnums.TRANSFER:
        return t('tooltip.transfer');
      case EndOfEmploymentSettlementEnums.PAY_OUT:
        return t('tooltip.pay_out');
      case EndOfEmploymentSettlementEnums.SETTLE:
        return t('tooltip.settle');
      default:
        return '';
    }
  })();

  const hoursFields: { name: keyof SettlementFormValues; labelKey: string }[] =
    [
      { name: 'extraHours', labelKey: 'extraHours' },
      { name: 'leaveHours', labelKey: 'leaveHours' },
      { name: 'plbHours', labelKey: 'plbHours' }
    ];

  // if transfer settlement is set and there are no valid work agreements, set the settlement type to NONE
  useEffect(() => {
    if (!!!validWorkAgreements && isTransferSettlementType) {
      setSettlementType(EndOfEmploymentSettlementEnums?.NONE?.toLowerCase?.());
      setValue(
        'endOfEmploymentSettlementType',
        EndOfEmploymentSettlementEnums?.NONE
      );
    } else {
      setSettlementType(
        watchEndOfEmploymentSettlementType?.toLowerCase?.() ??
          EndOfEmploymentSettlementEnums?.NONE?.toLowerCase?.()
      );
    }
  }, [
    validWorkAgreements,
    isTransferSettlementType,
    watchEndOfEmploymentSettlementType
  ]);

  return (
    <>
      <StyledBoxFlexCenter>
        {isLoading ? (
          <Skeleton width="100%" height="40px" />
        ) : (
          <>
            <ControlledToggleButtonGroup
              control={control}
              name="endOfEmploymentSettlementType"
              color="secondary"
              aria-label="alignment"
              inputToggleButtons={[
                {
                  label: t('settlementType.pay_out'),
                  value: EndOfEmploymentSettlementEnums.PAY_OUT
                },
                {
                  label: t('settlementType.settle'),
                  value: EndOfEmploymentSettlementEnums.SETTLE
                },
                ...(!!validWorkAgreements
                  ? [
                      {
                        label: t('settlementType.transfer'),
                        value: EndOfEmploymentSettlementEnums.TRANSFER
                      }
                    ]
                  : [])
              ]}
            />
            {hasSettlementType && (
              <Tooltip title={tooltipTitle}>
                <Icon type="circle-info" color="primary" fontSize="small" />
              </Tooltip>
            )}
          </>
        )}
      </StyledBoxFlexCenter>
      <StyledSettlementsDivider textAlign="center">
        {hasSettlementType && (
          <Typography
            variant="body2"
            color="inherit"
            sx={{ textTransform: 'uppercase' }}
          >
            {t('dividerText.settlementTypeText', {
              type: t(`dividerText.${settlementType}`)
            })}
          </Typography>
        )}
      </StyledSettlementsDivider>
      {isTransferSettlementType && !!validWorkAgreements && (
        <ControlledSelect
          control={control}
          name="validEmployment"
          label={t('validEmployment')}
          autoSelectSingleOption={false}
          options={
            validWorkAgreements?.map(
              ({ id, number, organizationalUnitName, functionName }) => {
                const label = [number, organizationalUnitName, functionName]
                  .filter(Boolean)
                  .join(' - ');

                return { label, value: id };
              }
            ) ?? []
          }
        />
      )}

      {isLoading && <Skeleton width="100%" height="40px" />}
      {hoursFields.map(({ name, labelKey }) => {
        const dutyCodeKey = buildDutyCodeKey(
          watchEndOfEmploymentSettlementType,
          name
        );

        const showWarning =
          dutyCodeKey !== undefined &&
          endOfEmploymentData?.[dutyCodeKey] == null;

        return (
          <StyledBoxFlexCenter key={name}>
            <ControlledReadOnlyField
              control={control}
              name={name}
              label={t(labelKey)}
              shouldShow={showWhenNonZero(watch(name))}
              isLoading={isLoading}
            />
            {showWarning &&
              hasSettlementType &&
              showWhenNonZero(watch(name)) && (
                <Tooltip title={t('tooltip.missingDutyCode')}>
                  <Icon
                    type="triangle-exclamation"
                    color="warning"
                    fontSize="medium"
                  />
                </Tooltip>
              )}
          </StyledBoxFlexCenter>
        );
      })}
    </>
  );
};

export default SelectSettlementType;
